/**
 * Schedule Visualization Dialog
 *
 * Displays a preview of the schedule visualization with format selection
 * and download functionality. Supports both PNG and PDF formats.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  CircularProgress,
  Alert,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useGenerateVisualizationMutation } from '../../../api/schedulingApi';
import { API_BASE_URL } from '../../../config/api';

interface ScheduleVisualizationDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  studioId: string;
  scheduleName: string;
}

const ScheduleVisualizationDialog: React.FC<
  ScheduleVisualizationDialogProps
> = ({ open, onClose, scheduleId, studioId, scheduleName }) => {
  const [format, setFormat] = useState<'png' | 'pdf'>('png');
  const [visualizationUrl, setVisualizationUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [generateVisualization, { isLoading }] =
    useGenerateVisualizationMutation();

  // Generate visualization when format changes
  const handleFormatChange = async (
    event: React.MouseEvent<HTMLElement>,
    newFormat: 'png' | 'pdf' | null
  ) => {
    if (newFormat === null) return; // Don't allow deselection

    setFormat(newFormat);
    setError(null);
    setVisualizationUrl(null);

    try {
      const result = await generateVisualization({
        id: scheduleId,
        studioId,
        format: newFormat,
      }).unwrap();

      // Prepend backend URL if needed
      const fullUrl = result.visualization_url.startsWith('http')
        ? result.visualization_url
        : `${API_BASE_URL}${result.visualization_url}`;

      setVisualizationUrl(fullUrl);
    } catch (err: any) {
      console.error('Failed to generate visualization:', err);
      setError(
        err?.data?.detail ||
          'Failed to generate visualization. Please try again.'
      );
    }
  };

  // Auto-generate on open
  React.useEffect(() => {
    if (open) {
      handleFormatChange(null as any, format);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Download the visualization
  const handleDownload = async () => {
    if (!visualizationUrl) return;

    try {
      // Fetch the file as a blob to force download
      const response = await fetch(visualizationUrl);
      const blob = await response.blob();

      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${scheduleName.replace(/\s+/g, '_')}_schedule.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download file:', err);
      setError('Failed to download file. Please try again.');
    }
  };

  // Close and reset state
  const handleClose = () => {
    setVisualizationUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Schedule Visualization</Typography>
          <Box>
            <ToggleButtonGroup
              value={format}
              exclusive
              onChange={handleFormatChange}
              size="small"
              disabled={isLoading}
            >
              <ToggleButton value="png" aria-label="PNG format">
                <ImageIcon sx={{ mr: 1 }} fontSize="small" />
                PNG
              </ToggleButton>
              <ToggleButton value="pdf" aria-label="PDF format">
                <PictureAsPdfIcon sx={{ mr: 1 }} fontSize="small" />
                PDF
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isLoading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
          >
            <CircularProgress size={48} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Generating {format.toUpperCase()} visualization...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isLoading && !error && visualizationUrl && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              backgroundColor: 'grey.100',
              borderRadius: 1,
              p: 2,
            }}
          >
            {format === 'png' ? (
              <img
                src={visualizationUrl}
                alt="Schedule visualization"
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box textAlign="center">
                <PictureAsPdfIcon sx={{ fontSize: 80, color: 'error.main' }} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  PDF Ready
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click &quot;Download&quot; to save the PDF file
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<CloseIcon />}>
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={isLoading || !visualizationUrl || !!error}
        >
          Download {format.toUpperCase()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleVisualizationDialog;
